import React, { useState, useEffect, useMemo } from 'react';
import { Search, X } from 'lucide-react';

/* ─── Constants ────────────────────────────────────────────────────── */

const ROLE_OPTIONS = [
  { value: 'cbv', label: 'Valuation Specialist (CBV)', plural: 'Valuation Specialists (CBV)' },
  { value: 'business_broker', label: 'Business Broker / M&A Advisor', plural: 'Business Brokers' },
  { value: 'lawyer', label: 'Lawyer', plural: 'Lawyers' },
  { value: 'accountant', label: 'Accountant / Tax Advisor', plural: 'Accountants' },
  { value: 'lender', label: 'Lender / Financing', plural: 'Lenders' },
  { value: 'cepa_coach', label: 'CEPA / Exit Planner', plural: 'Exit Planners (CEPA)' },
];

function deriveInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

// Per-card affordance based on the partner's intro state with the current
// seller. `accepted` is the only state where direct add-to-team is allowed;
// every other state falls through to a "Request Introduction" affordance
// that takes the seller to the partner profile (the proper place for that
// flow). `declined` is terminal — selection is permanently disabled.
function introAffordance(partner) {
  // Already on the seller's team — short-circuit. Same intent as the
  // partner-profile CTA: don't invite a duplicate-add the mutation would
  // reject, surface that the seat already exists.
  if (partner.on_my_team) {
    return { selectable: false, label: '✓ On your team', tone: 'success' };
  }
  switch (partner.intro_state) {
    case 'accepted':
      return { selectable: true, label: null };
    case 'pending':
    case 'matched':
      return { selectable: false, label: 'Awaiting response', tone: 'pending' };
    case 'declined':
      return { selectable: false, label: 'Declined', tone: 'declined' };
    case 'completed':
      return { selectable: false, label: 'Completed', tone: 'pending' };
    default:
      return { selectable: false, label: 'Request introduction first', tone: 'cta', linkToProfile: true };
  }
}

// Compute a recommendation badge for each partner based on user context.
// Returns null if no compelling badge applies.
function badgeFor(partner, userIndustry) {
  if (userIndustry && (partner.industry_tags || []).includes(userIndustry)) {
    return { label: `Match for ${userIndustry}`, color: 'orange' };
  }
  if ((partner.rating || 0) >= 4.8) {
    return { label: 'Top-rated', color: 'green' };
  }
  if (partner.verified) {
    return { label: 'Verified', color: 'blue' };
  }
  return null;
}

/* ─── Sub-components ──────────────────────────────────────────────── */

// Renders the intro-state-aware action for a partner card. `accepted` shows
// the selectable Add/Added toggle; everything else shows a status pill (with
// a link to the partner profile when the seller still needs to request an
// intro). Used by both the recommended (lg) and others (sm) lists.
function PartnerActionButton({ partner, isSelected, onToggle, size }) {
  const aff = introAffordance(partner);

  if (aff.selectable) {
    if (size === 'lg') {
      return (
        <button
          type="button"
          onClick={() => onToggle(partner.id)}
          className={`text-[13px] px-4 py-2 rounded-full font-medium flex-shrink-0 transition-colors ${
            isSelected
              ? 'border border-[hsl(var(--border))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))]'
              : 'bg-[hsl(var(--succeedo-orange))] text-white hover:opacity-90'
          }`}
        >
          {isSelected ? '✓ Added' : 'Add to Team'}
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => onToggle(partner.id)}
        className={`text-[12px] whitespace-nowrap transition-colors ${
          isSelected
            ? 'text-[hsl(var(--chart-2))] font-medium'
            : 'text-[hsl(var(--succeedo-orange))] hover:underline'
        }`}
      >
        {isSelected ? '✓ Added' : 'Add'}
      </button>
    );
  }

  if (aff.linkToProfile) {
    return (
      <a
        href={`/succeedo/partners/${partner.id}`}
        className="text-[12px] whitespace-nowrap text-[hsl(var(--succeedo-orange))] hover:underline"
      >
        {aff.label} →
      </a>
    );
  }

  let toneClass = 'text-[hsl(var(--muted-foreground))]';
  if (aff.tone === 'declined') toneClass = 'text-[hsl(var(--destructive))]';
  if (aff.tone === 'success') toneClass = 'text-[hsl(var(--chart-2))] font-medium';
  return (
    <span className={`text-[12px] whitespace-nowrap ${toneClass}`}>{aff.label}</span>
  );
}

function SelectionCard({ title, subtitle, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full text-left p-4 rounded-[12px] border-2 transition-all ${
        selected
          ? 'border-[hsl(var(--succeedo-orange))] bg-[hsl(var(--succeedo-orange))]/5'
          : 'border-[hsl(var(--border))] bg-[hsl(var(--card))] hover:border-[hsl(var(--muted-foreground))]/30'
      }`}
    >
      <div className="space-y-1">
        <div className="text-[14px] font-semibold text-[hsl(var(--foreground))]">{title}</div>
        <div className="text-[13px] text-[hsl(var(--muted-foreground))] leading-relaxed">{subtitle}</div>
      </div>
    </button>
  );
}

/* ─── AddPartnerModal ─────────────────────────────────────────────── */

const AddPartnerModal = ({
  isOpen,
  onClose,
  onAdd,
  onUpdate,
  onRequestIntroduction,
  defaultTab = 'existing',
  defaultRole = null,
  defaultRegion = '',
  defaultIndustry = '',
  partners = [],
  replaceTargetId = null,
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab);

  // Tab 1: "I Have Someone" state
  const [existingName, setExistingName] = useState('');
  const [existingFirm, setExistingFirm] = useState('');
  const [existingEmail, setExistingEmail] = useState('');
  const [existingPhone, setExistingPhone] = useState('');
  const [existingRole, setExistingRole] = useState(defaultRole || '');
  const [inviteOption, setInviteOption] = useState('record');

  // Tab 2: "Find an Expert" state
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  // Default region to user's province (hard filter — owners want local).
  // Default industry to "Any" (soft filter — owners don't want their industry to
  // hide otherwise-relevant partners). User's industry still drives the
  // "Match for [Industry]" recommendation badge below.
  const [regionFilter, setRegionFilter] = useState(defaultRegion || 'Any');
  const [industryFilter, setIndustryFilter] = useState('Any');
  const [selectedPartners, setSelectedPartners] = useState([]);

  // Tab 3: "Invite by Email" state
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState(defaultRole || '');
  const [inviteMessage, setInviteMessage] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [introRequested, setIntroRequested] = useState(false);
  const [introError, setIntroError] = useState(null);

  // Update tab and role when props change
  useEffect(() => {
    if (isOpen) {
      setActiveTab(defaultTab);
      if (defaultRole) {
        setExistingRole(defaultRole);
        setInviteRole(defaultRole);
      }
      setRegionFilter(defaultRegion || 'Any');
      setIndustryFilter('Any');
      setIntroRequested(false);
      setIntroError(null);
    }
  }, [isOpen, defaultTab, defaultRole, defaultRegion, defaultIndustry]);

  const resetForm = () => {
    setExistingName('');
    setExistingFirm('');
    setExistingEmail('');
    setExistingPhone('');
    setExistingRole(defaultRole || '');
    setInviteOption('record');
    setSearchQuery('');
    setShowSearch(false);
    setSelectedPartners([]);
    setInviteEmail('');
    setInviteRole(defaultRole || '');
    setInviteMessage('');
  };

  // Derive filter options from the actual partners (matches PartnerDirectoryPage pattern).
  const regionOptions = useMemo(() => {
    const set = new Set();
    partners.forEach((p) => (p.geography_regions || []).forEach((r) => set.add(r)));
    return ['Any', ...Array.from(set).sort()];
  }, [partners]);

  const industryOptions = useMemo(() => {
    const set = new Set();
    partners.forEach((p) => (p.industry_tags || []).forEach((t) => set.add(t)));
    return ['Any', ...Array.from(set).sort()];
  }, [partners]);

  // Filter partners by role, region (array.includes), industry (array.includes), search
  const filteredPartners = useMemo(() => {
    return partners.filter((p) => {
      if (defaultRole && p.partner_category !== defaultRole) return false;
      if (regionFilter !== 'Any' && !(p.geography_regions || []).includes(regionFilter)) return false;
      if (industryFilter !== 'Any' && !(p.industry_tags || []).includes(industryFilter)) return false;
      if (showSearch && searchQuery) {
        const q = searchQuery.toLowerCase();
        const matches =
          (p.name || '').toLowerCase().includes(q) ||
          (p.org_name || '').toLowerCase().includes(q);
        if (!matches) return false;
      }
      return true;
    });
  }, [partners, defaultRole, regionFilter, industryFilter, showSearch, searchQuery]);

  // Compute recommendation badges and split into recommended (top 2 with badge) + others
  const partnersWithBadges = useMemo(
    () => filteredPartners.map((p) => ({ ...p, _badge: badgeFor(p, defaultIndustry) })),
    [filteredPartners, defaultIndustry]
  );
  const recommendedPartners = partnersWithBadges
    .filter((p) => p._badge)
    .sort((a, b) => (b.rating || 0) - (a.rating || 0))
    .slice(0, 2);
  const recommendedIds = new Set(recommendedPartners.map((p) => p.id));
  const otherPartners = partnersWithBadges.filter((p) => !recommendedIds.has(p.id));

  const hasActiveFilters =
    regionFilter !== (defaultRegion || 'Any') ||
    industryFilter !== 'Any';

  const currentRoleDisplay = defaultRole
    ? (ROLE_OPTIONS.find((r) => r.value === defaultRole)?.plural || 'Partners')
    : 'Partners';

  const togglePartner = (partnerId) => {
    setSelectedPartners((prev) =>
      prev.includes(partnerId) ? prev.filter((id) => id !== partnerId) : [...prev, partnerId]
    );
  };

  const handleAddToTeam = async () => {
    if (submitting) return;
    setSubmitting(true);

    try {
      if (activeTab === 'existing') {
        const status = inviteOption === 'invite' ? 'invited' : 'added';
        const variables = {
          role: existingRole,
          status,
          name: existingName,
          email: existingEmail || null,
          phone: existingPhone || null,
          firm: existingFirm || null,
        };
        if (replaceTargetId && onUpdate) {
          await onUpdate({ id: replaceTargetId, ...variables });
          if (window.ahoy) {
            window.ahoy.track('succeedo_team_member_updated', { tab: 'existing', role: existingRole, replace: true });
          }
        } else {
          await onAdd(variables);
          if (window.ahoy) {
            window.ahoy.track('succeedo_team_member_added', { tab: 'existing', role: existingRole });
          }
        }
      } else if (activeTab === 'directory') {
        // Directory adds are gated on intro_state === 'accepted' both in the
        // UI (selection disabled below) and on the server (addAdvisoryTeamMember
        // rejects non-accepted partners). Status is forced to 'active' on the
        // server for directory paths regardless of what we send here, but we
        // send 'active' for clarity.
        const selected = partners.filter(
          (p) => selectedPartners.includes(p.id) && p.intro_state === 'accepted',
        );
        for (const partner of selected) {
          await onAdd({
            role: partner.partner_category,
            status: 'active',
            name: partner.name,
            firm: partner.org_name || null,
            email: partner.contact_email || null,
            coachProfileId: partner.id,
          });
        }
        if (window.ahoy) {
          window.ahoy.track('succeedo_team_member_added', {
            tab: 'directory',
            count: selected.length,
          });
        }
      } else if (activeTab === 'invite') {
        await onAdd({
          role: inviteRole,
          status: 'invited',
          name: '',
          email: inviteEmail,
          notes: inviteMessage || null,
        });
        if (window.ahoy) {
          window.ahoy.track('succeedo_team_member_added', { tab: 'invite', role: inviteRole });
        }
      }

      resetForm();
      onClose();
    } catch (err) {
      console.error('Failed to add team member:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetFilters = () => {
    setRegionFilter(defaultRegion || 'Any');
    setIndustryFilter('Any');
  };

  const handleRequestIntroduction = async () => {
    if (!onRequestIntroduction || !defaultRole) return;
    setIntroError(null);
    try {
      await onRequestIntroduction(defaultRole);
      setIntroRequested(true);
    } catch (err) {
      setIntroError(err?.message || 'Could not submit your request. Please try again.');
    }
  };

  const isTab1Valid = existingName.trim() !== '' && existingRole !== '';
  const isTab3Valid = inviteEmail.trim() !== '' && inviteRole !== '';
  const isTab2Valid = selectedPartners.length > 0;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-[hsl(var(--background))]/60 backdrop-blur-[10px]"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[16px] p-6 max-w-[520px] w-full mx-4 space-y-6 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-start justify-between">
          <h2 className="text-[18px] font-semibold text-[hsl(var(--foreground))]">
            Add to Your Team
          </h2>
          <button
            onClick={onClose}
            className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-6 border-b border-[hsl(var(--border))]">
          <button
            onClick={() => setActiveTab('existing')}
            className={`font-mono text-[11px] uppercase pb-3 transition-colors ${
              activeTab === 'existing'
                ? 'text-[hsl(var(--foreground))] border-b-2 border-[hsl(var(--succeedo-orange))]'
                : 'text-[hsl(var(--muted-foreground))]'
            }`}
          >
            I HAVE SOMEONE
          </button>
          <button
            onClick={() => setActiveTab('directory')}
            className={`font-mono text-[11px] uppercase pb-3 transition-colors ${
              activeTab === 'directory'
                ? 'text-[hsl(var(--foreground))] border-b-2 border-[hsl(var(--succeedo-orange))]'
                : 'text-[hsl(var(--muted-foreground))]'
            }`}
          >
            FIND AN EXPERT
          </button>
          <button
            onClick={() => setActiveTab('invite')}
            className={`font-mono text-[11px] uppercase pb-3 transition-colors ${
              activeTab === 'invite'
                ? 'text-[hsl(var(--foreground))] border-b-2 border-[hsl(var(--succeedo-orange))]'
                : 'text-[hsl(var(--muted-foreground))]'
            }`}
          >
            INVITE BY EMAIL
          </button>
        </div>

        {/* Content */}
        {activeTab === 'existing' ? (
          <div className="space-y-4">
            {/* Role Selection */}
            <div className="space-y-2">
              <label className="text-[13px] text-[hsl(var(--muted-foreground))]">
                Role <span className="text-[hsl(var(--destructive))]">*</span>
              </label>
              <select
                value={existingRole}
                onChange={(e) => setExistingRole(e.target.value)}
                className="w-full bg-[hsl(var(--muted))] border border-[hsl(var(--input))] rounded-[8px] px-4 py-3 text-[14px] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20"
              >
                <option value="">Select a role...</option>
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Name Input */}
            <div className="space-y-2">
              <label className="text-[13px] text-[hsl(var(--muted-foreground))]">
                Their name <span className="text-[hsl(var(--destructive))]">*</span>
              </label>
              <input
                type="text"
                placeholder="e.g., Sarah Chen"
                value={existingName}
                onChange={(e) => setExistingName(e.target.value)}
                className="w-full bg-[hsl(var(--muted))] border border-[hsl(var(--input))] rounded-[8px] px-4 py-3 text-[14px] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20"
              />
            </div>

            {/* Firm Input */}
            <div className="space-y-2">
              <label className="text-[13px] text-[hsl(var(--muted-foreground))]">
                Firm or company name
              </label>
              <input
                type="text"
                placeholder="e.g., Collins & Partners"
                value={existingFirm}
                onChange={(e) => setExistingFirm(e.target.value)}
                className="w-full bg-[hsl(var(--muted))] border border-[hsl(var(--input))] rounded-[8px] px-4 py-3 text-[14px] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20"
              />
            </div>

            {/* Email Input */}
            <div className="space-y-2">
              <label className="text-[13px] text-[hsl(var(--muted-foreground))]">
                Email address
              </label>
              <input
                type="email"
                placeholder="sarah@collinspartners.ca"
                value={existingEmail}
                onChange={(e) => setExistingEmail(e.target.value)}
                className="w-full bg-[hsl(var(--muted))] border border-[hsl(var(--input))] rounded-[8px] px-4 py-3 text-[14px] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20"
              />
            </div>

            {/* Phone Input */}
            <div className="space-y-2">
              <label className="text-[13px] text-[hsl(var(--muted-foreground))]">
                Phone number
              </label>
              <input
                type="tel"
                placeholder="(416) 555-0123"
                value={existingPhone}
                onChange={(e) => setExistingPhone(e.target.value)}
                className="w-full bg-[hsl(var(--muted))] border border-[hsl(var(--input))] rounded-[8px] px-4 py-3 text-[14px] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20"
              />
            </div>

            {/* Choice Section */}
            <div className="space-y-3 pt-2">
              <label className="text-[13px] text-[hsl(var(--muted-foreground))]">
                What would you like to do?
              </label>
              <div className="space-y-2">
                <SelectionCard
                  title="Invite them to Succeedo"
                  subtitle="They'll get an email to join your team on the platform. You can share documents and message directly."
                  selected={inviteOption === 'invite'}
                  onClick={() => setInviteOption('invite')}
                />
                <SelectionCard
                  title="Just add to my records"
                  subtitle="Record that you work with them. We won't contact them. You can invite them later."
                  selected={inviteOption === 'record'}
                  onClick={() => setInviteOption('record')}
                />
              </div>
            </div>
          </div>
        ) : activeTab === 'directory' ? (
          <div className="space-y-4">
            {/* Header Strip */}
            <div className="space-y-1">
              <div className="font-mono text-[11px] uppercase text-[hsl(var(--muted-foreground))]/60 tracking-wide">
                BROWSING: {currentRoleDisplay.toUpperCase()}
              </div>
              <p className="text-[13px] text-[hsl(var(--muted-foreground))]">
                Vetted professionals matched to your business based on your assessment and industry.
              </p>
            </div>

            {filteredPartners.length === 0 ? (
              /* Empty State */
              <div className="py-8 px-6 text-center space-y-4">
                <div className="w-12 h-12 mx-auto rounded-full bg-[hsl(var(--muted))]/50 flex items-center justify-center">
                  <Search className="w-6 h-6 text-[hsl(var(--muted-foreground))]/40" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-[16px] font-semibold text-[hsl(var(--foreground))]">
                    We're still vetting {currentRoleDisplay} in {regionFilter}
                  </h3>
                  <p className="text-[13px] text-[hsl(var(--muted-foreground))] max-w-[380px] mx-auto">
                    We hand-pick partners to make sure they're a fit. While we work on it, you can:
                  </p>
                </div>
                <div className="space-y-2 pt-2">
                  <button
                    onClick={() => setActiveTab('invite')}
                    className="w-full bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-2 text-[14px] font-medium hover:opacity-90 transition-opacity"
                  >
                    Invite someone by email
                  </button>
                  {introRequested ? (
                    <div className="w-full text-center px-5 py-2 text-[13px] text-[hsl(var(--chart-2))]">
                      ✓ Request submitted. Our team will reach out shortly.
                    </div>
                  ) : (
                    <button
                      onClick={handleRequestIntroduction}
                      disabled={!onRequestIntroduction || !defaultRole}
                      className="w-full border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-full px-5 py-2 text-[14px] hover:bg-[hsl(var(--accent))] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Request an introduction
                    </button>
                  )}
                  {introError && (
                    <p className="text-[12px] text-[hsl(var(--destructive))]">{introError}</p>
                  )}
                </div>
              </div>
            ) : (
              <>
                {/* Recommended Matches */}
                {recommendedPartners.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-[14px] font-semibold text-[hsl(var(--foreground))]">
                      Best matches for you
                    </h3>
                    <div className="space-y-3">
                      {recommendedPartners.map((partner) => {
                        const initials = deriveInitials(partner.name);
                        const region = (partner.geography_regions || [])[0];
                        const isSelected = selectedPartners.includes(partner.id);
                        return (
                          <div
                            key={partner.id}
                            className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-[12px] p-4 shadow-sm hover:shadow-md transition-shadow"
                          >
                            <div className="flex items-start gap-4">
                              {partner.avatar_url ? (
                                <img src={partner.avatar_url} alt={partner.name} className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center flex-shrink-0">
                                  <span className="text-[hsl(var(--foreground))] font-semibold text-[14px]">
                                    {initials}
                                  </span>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="text-[14px] font-medium text-[hsl(var(--foreground))] mb-1">
                                  {partner.name}
                                </div>
                                <div className="text-[12px] text-[hsl(var(--muted-foreground))] mb-2">
                                  {[partner.org_name, region].filter(Boolean).join(' · ')}
                                  {partner.rating ? ` · ★${partner.rating}` : ''}
                                </div>
                                {partner._badge && (
                                  <span
                                    className={`inline-block text-[11px] font-medium px-2 py-1 rounded-full ${
                                      partner._badge.color === 'orange'
                                        ? 'bg-[hsl(var(--succeedo-orange))]/10 text-[hsl(var(--succeedo-orange))]'
                                        : partner._badge.color === 'green'
                                        ? 'bg-[hsl(var(--chart-2))]/10 text-[hsl(var(--chart-2))]'
                                        : 'bg-[hsl(var(--chart-1))]/10 text-[hsl(var(--chart-1))]'
                                    }`}
                                  >
                                    {partner._badge.label}
                                  </span>
                                )}
                                {partner.bio && (
                                  <p className="text-[12px] text-[hsl(var(--muted-foreground))]/60 mt-2 line-clamp-1">
                                    {partner.bio}
                                  </p>
                                )}
                              </div>
                              <PartnerActionButton
                                partner={partner}
                                isSelected={isSelected}
                                onToggle={togglePartner}
                                size="lg"
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Filters Row */}
                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <select
                      value={regionFilter}
                      onChange={(e) => setRegionFilter(e.target.value)}
                      className="text-[12px] bg-[hsl(var(--muted))] border border-[hsl(var(--border))] rounded-full px-3 py-1.5 text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20"
                    >
                      {regionOptions.map((r) => (
                        <option key={r} value={r}>{r === 'Any' ? 'Any region' : r}</option>
                      ))}
                    </select>
                    <select
                      value={industryFilter}
                      onChange={(e) => setIndustryFilter(e.target.value)}
                      className="text-[12px] bg-[hsl(var(--muted))] border border-[hsl(var(--border))] rounded-full px-3 py-1.5 text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20"
                    >
                      {industryOptions.map((i) => (
                        <option key={i} value={i}>{i === 'Any' ? 'Any industry' : i}</option>
                      ))}
                    </select>
                  </div>
                  {hasActiveFilters && (
                    <button
                      onClick={handleResetFilters}
                      className="text-[12px] text-[hsl(var(--succeedo-orange))] hover:underline"
                    >
                      Reset filters
                    </button>
                  )}
                </div>

                {/* All Partners List */}
                {otherPartners.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-[13px] text-[hsl(var(--muted-foreground))]">
                      All {currentRoleDisplay} in your network
                    </h3>
                    <div className="space-y-2 max-h-[320px] overflow-y-auto">
                      {otherPartners.map((partner) => {
                        const initials = deriveInitials(partner.name);
                        const region = (partner.geography_regions || [])[0];
                        const isSelected = selectedPartners.includes(partner.id);
                        return (
                          <div
                            key={partner.id}
                            className="bg-[hsl(var(--muted))]/30 rounded-[10px] p-3 border border-[hsl(var(--border))]/30 flex items-center justify-between gap-3"
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {partner.avatar_url ? (
                                <img src={partner.avatar_url} alt={partner.name} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center flex-shrink-0">
                                  <span className="text-[hsl(var(--foreground))] font-semibold text-[12px]">
                                    {initials}
                                  </span>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="text-[13px] text-[hsl(var(--foreground))] font-medium truncate">
                                  {partner.name}
                                </div>
                                <div className="text-[11px] text-[hsl(var(--muted-foreground))]/60 truncate">
                                  {[partner.org_name, region].filter(Boolean).join(' · ')}
                                </div>
                              </div>
                            </div>
                            <PartnerActionButton
                              partner={partner}
                              isSelected={isSelected}
                              onToggle={togglePartner}
                              size="sm"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Search as Disclosure */}
                <div className="pt-2 border-t border-[hsl(var(--border))]">
                  {!showSearch ? (
                    <button
                      onClick={() => setShowSearch(true)}
                      className="text-[12px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--succeedo-orange))] transition-colors"
                    >
                      Looking for someone specific? Search by name →
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]/35" />
                        <input
                          type="text"
                          placeholder="Search by name or firm..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full bg-[hsl(var(--muted))] border border-[hsl(var(--input))] rounded-[8px] pl-10 pr-4 py-2 text-[13px] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20"
                        />
                      </div>
                      <button
                        onClick={() => {
                          setShowSearch(false);
                          setSearchQuery('');
                        }}
                        className="text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"
                      >
                        Hide search
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {/* Email Input */}
            <div className="space-y-2">
              <label className="text-[13px] text-[hsl(var(--muted-foreground))]">
                Email Address <span className="text-[hsl(var(--destructive))]">*</span>
              </label>
              <input
                type="email"
                placeholder="partner@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="w-full bg-[hsl(var(--muted))] border border-[hsl(var(--input))] rounded-[8px] px-4 py-3 text-[14px] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20"
              />
            </div>

            {/* Role Selection */}
            <div className="space-y-2">
              <label className="text-[13px] text-[hsl(var(--muted-foreground))]">
                Role <span className="text-[hsl(var(--destructive))]">*</span>
              </label>
              <select
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                className="w-full bg-[hsl(var(--muted))] border border-[hsl(var(--input))] rounded-[8px] px-4 py-3 text-[14px] text-[hsl(var(--foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20"
              >
                <option value="">Select a role...</option>
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Message */}
            <div className="space-y-2">
              <label className="text-[13px] text-[hsl(var(--muted-foreground))]">
                Personal Message (Optional)
              </label>
              <textarea
                placeholder="Add a personal note to your invitation..."
                rows={4}
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                className="w-full bg-[hsl(var(--muted))] border border-[hsl(var(--input))] rounded-[8px] px-4 py-3 text-[14px] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-[hsl(var(--succeedo-orange))]/20 resize-none"
              />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={onClose}
            className="border border-[hsl(var(--border))] text-[hsl(var(--foreground))] rounded-full px-5 py-2 text-[14px] hover:bg-[hsl(var(--accent))] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAddToTeam}
            disabled={
              submitting ||
              (activeTab === 'existing' && !isTab1Valid) ||
              (activeTab === 'directory' && !isTab2Valid) ||
              (activeTab === 'invite' && !isTab3Valid)
            }
            className="bg-[hsl(var(--succeedo-orange))] text-white rounded-full px-5 py-2 text-[14px] font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting
              ? (replaceTargetId ? 'Replacing...' : 'Adding...')
              : activeTab === 'existing'
              ? replaceTargetId
                ? 'Replace'
                : inviteOption === 'invite'
                ? 'Add & Send Invite'
                : 'Add to Team'
              : activeTab === 'directory'
              ? `Add to Team${selectedPartners.length > 1 ? ` (${selectedPartners.length})` : ''}`
              : 'Send Invitation'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPartnerModal;
